import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { NormalizedTeamMembersAllocations } from '../model/types';

interface ILocationGroupEntry {
  clients: Array<{ id: number; name: string }>;
}

interface IFormValuesBase {
  teamMembersAllocations: NormalizedTeamMembersAllocations[];
}

interface IProps {
  teamMemberIdx: number;
  locationGroups?: ILocationGroupEntry[];
}

const useAllocationConflictMessage = ({ teamMemberIdx, locationGroups = [] }: IProps) => {
  const { watch } = useFormContext<IFormValuesBase>();

  const selectedClientsIds = watch(`teamMembersAllocations.${teamMemberIdx}.selectedClientsIds`);
  const selectedLocationGroupClientIds = watch(
    `teamMembersAllocations.${teamMemberIdx}.selectedLocationGroupClientIds`,
  );

  const clientListConflict = useMemo(() => {
    const clients = selectedClientsIds ?? [];
    const lgMap: Record<number, string> = selectedLocationGroupClientIds ?? {};
    const groupNames = Array.from(new Set(clients.filter(id => lgMap[id]).map(id => lgMap[id])));
    return groupNames.length > 0
      ? `This user already has allocations created by ${groupNames.join(
          ', ',
        )}. Please use the same grouping type to edit this user's allocation.`
      : null;
  }, [selectedClientsIds, selectedLocationGroupClientIds]);

  const locationGroupListConflict = useMemo(() => {
    const lgClientIds = new Set(Object.keys(selectedLocationGroupClientIds ?? {}).map(Number));
    const conflictingIds = (selectedClientsIds ?? []).filter(id => lgClientIds.has(id));
    if (conflictingIds.length === 0) return null;
    const allClients = locationGroups.flatMap(lg => lg.clients);
    const clientNames = Array.from(
      new Set(
        conflictingIds
          .map(id => allClients.find(c => c.id === id)?.name)
          .filter((n): n is string => n !== undefined),
      ),
    );
    const namesStr = clientNames.length > 0 ? clientNames.join(', ') : 'Client';
    return `This user already has allocations created by ${namesStr}. Please use the same grouping type to edit this user's allocation.`;
  }, [selectedClientsIds, selectedLocationGroupClientIds, locationGroups]);

  return { clientListConflict, locationGroupListConflict };
};

export default useAllocationConflictMessage;
